import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { useContent } from '../contexts/ContentContext';
import Modal from './Modal';

type Props = {
    title: string;
    href?: string;
    root?: string;
    className?: string;
    headerContent?: React.ReactNode;
    footerContent?: React.ReactNode;
    children: (expanded: boolean) => React.ReactNode;
};

export default function ExploreSection({ title, href, root, className = '', headerContent, footerContent, children }: Props) {
    const { t } = useContent();
    const track = useRef<HTMLDivElement>(null);
    const [bounds, setBounds] = useState({ overflow: false, start: true, end: true });
    const [expanded, setExpanded] = useState(false);
    const measure = useCallback(() => {
        const element = track.current;
        if (!element) return;
        const next = { overflow: element.scrollWidth > element.clientWidth + 1, start: element.scrollLeft <= 1,
            end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 1 };
        setBounds(previous => Object.keys(next).every(key => previous[key as keyof typeof next] === next[key as keyof typeof next]) ? previous : next);
    }, []);
    useLayoutEffect(() => {
        const element = track.current;
        if (!element) return;
        const resize = new ResizeObserver(measure);
        resize.observe(element);
        const mutation = new MutationObserver(measure);
        mutation.observe(element, { childList: true, subtree: true });
        measure();
        return () => { resize.disconnect(); mutation.disconnect(); };
    }, [measure]);
    const move = (direction: -1 | 1) => {
        const element = track.current;
        if (!element) return;
        element.scrollBy({ left: direction * Math.max(100, element.clientWidth * 0.8),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    return <section className={`lab-explore-section ${className}`} data-discovery-root={root}>
        <header className="lab-explore-heading">
            <h3>{href ? <a href={href}>{title}<ChevronRight aria-hidden="true" /></a> : <span>{title}<ChevronRight aria-hidden="true" /></span>}</h3>
            {headerContent}
            {bounds.overflow && <button type="button" className="lab-explore-expand" aria-label={t('illustrations.expandCards', { title })}
                title={t('illustrations.expandCards', { title })} onClick={() => setExpanded(true)}><Maximize2 aria-hidden="true" /></button>}
        </header>
        <div className="lab-explore-window" data-overflow={bounds.overflow || undefined}>
            <div ref={track} className="lab-explore-track" tabIndex={0} role="group" aria-label={title} onScroll={measure}
                onKeyDown={event => {
                    if (event.target !== event.currentTarget || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                    event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
                }} onFocusCapture={event => {
                    const element = track.current, target = event.target as HTMLElement;
                    if (!element || target === element) return;
                    const area = element.getBoundingClientRect(), focused = target.getBoundingClientRect();
                    if (focused.left < area.left + 44) element.scrollLeft -= area.left + 44 - focused.left;
                    else if (focused.right > area.right - 44) element.scrollLeft += focused.right - area.right + 44;
                }}>{children(false)}</div>
            {bounds.overflow && <>
                <button type="button" className="lab-explore-prev" disabled={bounds.start} aria-label={t('illustrations.previousCards')} onClick={() => move(-1)}><ChevronLeft aria-hidden="true" /></button>
                <button type="button" className="lab-explore-next" disabled={bounds.end} aria-label={t('illustrations.nextCards')} onClick={() => move(1)}><ChevronRight aria-hidden="true" /></button>
            </>}
        </div>
        {footerContent}
        <Modal isOpen={expanded} onClose={() => setExpanded(false)} title={title} panelClassName="lab-explore-modal">
            <div className="lab-explore-expanded">{children(true)}</div>
        </Modal>
    </section>;
}
